const own = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);
const text = value => String(value ?? "").trim();
const assertMap = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid quotation settings map');
  for (const key of Object.keys(value)) if (['__proto__', 'constructor', 'prototype'].includes(key) || key.length > 2500) throw new Error('Invalid quotation settings key');
};
export const HEM_FIELDS = ["topHemIn", "bottomHemIn", "sideAllowancePerSideIn"];
export function getQuoteCuttingInputSignature(quote = {}) {
  return JSON.stringify([quote.quoteFullnessOverrides || {}, quote.fabricHemOverrides || {},
    quote.repeatSettings || {}, quote.useHalfPieceReuse !== false,
    (quote.rows || []).map(row => [row.id || row.rowId, getQuoteFabricIdentity(row), row.productId, row.productType,
      row.width, row.height, row.fabricWidthIn, row.qty, row.quantity, row.fullness,
      row.stitchingStyleId, row.stitchingConfigId, row.stitchingStyle, row.stitch,
      row.measurementUnit, row.unit, row.productConfigSnapshot?.fabricRules]),
  ]);
}
const optionalNumber = (value, max = 1000000) => {
  if (value === "" || value == null) return null;
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0 || number > max) throw new Error("Invalid quotation calculation override");
  return number;
};

// Price-rule labels are deliberately excluded from physical fabric identity.
export function getQuoteFabricIdentity(row = {}) {
  const sr = text(row.srNumber ?? row.srNo ?? row.sr);
  return JSON.stringify([text(row.brandId || row.fabricBrandId || row.brandName || row.fabricBrandName),
    text(row.catalogueId || row.catalogueName), sr,
    sr ? "" : text(row.fabricCodeRaw || row.fabricCode || row.fabricName || row.fabric)]);
}
export const getQuoteConstructionKey = (row = {}) => JSON.stringify([
  getQuoteFabricIdentity(row), text(row.productId || row.productType || row.productName || row.product),
]);
export function rebaseQuoteConstructionIdentities(quote, sourceRows, savedRows) {
  const normalized = normalizeQuoteCalculationSettings(quote);
  const hems = { ...normalized.fabricHemOverrides };
  const snapshot = normalized.quoteCalculationSettingsSnapshot;
  const rules = { ...(snapshot.fabricRules || {}) };
  const origins = new Map();
  for (const [index, row] of savedRows.entries()) {
    const ids = [row.id, row.rowId, row.sourceRowId, row.originalRowId].filter(Boolean).map(String);
    const source = sourceRows.find(candidate => [candidate.id, candidate.rowId].filter(Boolean)
      .some(id => ids.includes(String(id)) || ids.includes(`row_${id}`) || ids.includes(`accessory_${id}`)))
      || sourceRows[Number.isInteger(row.rowOrder) ? row.rowOrder : index];
    if (!source) throw new Error('Review quotation row identities before saving.');
    const oldFabric = getQuoteFabricIdentity(source), newFabric = getQuoteFabricIdentity(row);
    const oldConstruction = getQuoteConstructionKey(source), newConstruction = getQuoteConstructionKey(row);
    if (origins.has(newFabric) && origins.get(newFabric) !== oldFabric) throw new Error('Two fabrics would share an identity after catalogue cleanup. Review their exact codes before saving.');
    origins.set(newFabric, oldFabric);
    if (oldFabric !== newFabric && own(hems, oldFabric)) { hems[newFabric] = hems[oldFabric]; delete hems[oldFabric]; }
    if (oldConstruction !== newConstruction && own(rules, oldConstruction)) { rules[newConstruction] = rules[oldConstruction]; delete rules[oldConstruction]; }
  }
  return { ...normalized, fabricHemOverrides: hems, quoteCalculationSettingsSnapshot:
    Object.keys(snapshot).length ? { ...snapshot, fabricRules: rules } : snapshot };
}
export const getQuoteStitchingKey = (row = {}, selected = {}) => text(row.stitchingStyleId || row.stitchingConfigId || selected?.config?.id || selected?.key || row.stitchingStyle || row.stitch);
export const getQuoteTrackKey = (row = {}, selected = {}) => text(
  row.trackConfigId || row.trackTypeId || row.trackId || selected?.config?.trackConfigId || selected?.config?.trackTypeId ||
  selected?.config?.trackType || getQuoteStitchingKey(row, selected)
);

export function normalizeQuoteCalculationSettings(quote = {}) {
  const track = {}, hems = {};
  const trackInput = quote.quoteTrackRateOverrides || {};
  const hemInput = quote.fabricHemOverrides || {};
  assertMap(trackInput); assertMap(hemInput);
  if (Object.keys(trackInput).length > 150 || Object.keys(hemInput).length > 200) throw new Error("Too many quotation calculation overrides");
  for (const [key, value] of Object.entries(trackInput)) {
    const rate = optionalNumber(value?.rate);
    if (rate != null) track[key] = { rate, unit: text(value.unit || "rft"), source: "quote_override" };
  }
  for (const [key, value] of Object.entries(hemInput)) {
    const entry = {};
    for (const field of HEM_FIELDS) {
      const number = optionalNumber(value?.[field], 100);
      if (number != null) entry[field] = number;
    }
    if (Object.keys(entry).length) hems[key] = entry;
  }
  return { quoteTrackRateOverrides: track, fabricHemOverrides: hems,
    quoteCalculationSettingsSnapshot: quote.quoteCalculationSettingsSnapshot || {} };
}

export function validateQuoteCalculationSettings(quote = {}) {
  assertMap(quote.quoteFullnessOverrides || {});
  if (Object.keys(quote.quoteFullnessOverrides || {}).length > 150) throw new Error("Too many fullness overrides");
  for (const value of Object.values(quote.quoteFullnessOverrides || {})) {
    const fullness = optionalNumber(typeof value === "object" ? value?.quoteFullness : value, 10);
    if (fullness == null || fullness <= 0) throw new Error("Invalid quote fullness");
  }
  const normalized = normalizeQuoteCalculationSettings(quote);
  const snapshot = normalized.quoteCalculationSettingsSnapshot;
  assertMap(snapshot); assertMap(snapshot.fabricRules || {}); assertMap(snapshot.stitchingConfigs || {});
  if (JSON.stringify(normalized).length > 180000) throw new Error("Quotation settings snapshot is too large");
  for (const rules of Object.values(snapshot.fabricRules || {})) {
    assertMap(rules);
    for (const field of HEM_FIELDS) if (rules[field] != null) optionalNumber(rules[field], 100);
    if (rules.dropAllowanceIn != null) optionalNumber(rules.dropAllowanceIn, 200);
  }
  for (const config of Object.values(snapshot.stitchingConfigs || {})) {
    assertMap(config);
    if (config.trackRateFt != null) optionalNumber(config.trackRateFt);
    if (config.fullness != null) {
      const fullness = optionalNumber(config.fullness, 10);
      if (fullness <= 0) throw new Error("Invalid saved fullness");
    }
  }
  return normalized;
}

export function resolveQuoteStitchingConfig(row, selected, quote = {}) {
  const saved = quote.quoteCalculationSettingsSnapshot?.stitchingConfigs?.[getQuoteStitchingKey(row, selected)];
  const config = { ...(saved || selected?.config || {}) };
  const key = getQuoteTrackKey(row, { ...selected, config });
  const override = quote.quoteTrackRateOverrides?.[key];
  const unit = text(config.trackRateUnit || "rft");
  const hasOverride = override && optionalNumber(override.rate) != null;
  if (hasOverride && text(override.unit || "rft") !== unit) throw new Error("Track rate unit does not match this track");
  return { ...selected, config: { ...config,
    quoteTrackDefaultRateFt: Number(config.trackRateFt || 0),
    ...(hasOverride ? { trackRateFt: Number(override.rate) } : {}),
    quoteTrackRateSource: hasOverride ? "quote_override" : saved ? "quote_snapshot" : "settings_default",
    quoteStitchingDefaultSource: saved ? "quote_snapshot" : "settings_default",
  }, trackKey: key };
}

export function resolveQuoteHemRules(row, config, baseRules, quote = {}) {
  const saved = quote.quoteCalculationSettingsSnapshot?.fabricRules?.[getQuoteConstructionKey(row)];
  const base = { ...(saved || baseRules) };
  const supported = base.enabled !== false && ["curtain_panels", "roman_panels"].includes(base.meterFormulaType);
  const override = supported ? quote.fabricHemOverrides?.[getQuoteFabricIdentity(row)] : null;
  const result = { ...base, hemSource: !supported ? "not_applicable" : saved ? "quote_snapshot" : "settings_default" };
  if (override) {
    for (const field of HEM_FIELDS) if (own(override, field) && override[field] != null) result[field] = optionalNumber(override[field], 100);
    result.hemSource = "quote_override";
    if (own(override, "topHemIn") || own(override, "bottomHemIn")) result.dropAllowanceIn = Number(result.topHemIn || 0) + Number(result.bottomHemIn || 0);
  }
  result.noBottomHem = supported && Number(result.bottomHemIn) === 0;
  return result;
}

export function captureQuoteCalculationSettings({ rows, quote = {}, getProductConfig, getFabricRules, getStitchingStyle }) {
  const fabricRules = {}, stitchingConfigs = {};
  for (const row of rows || []) {
    const config = getProductConfig(row);
    const rules = getFabricRules(row, config);
    if (rules?.enabled) fabricRules[getQuoteConstructionKey(row)] = quote.quoteCalculationSettingsSnapshot?.fabricRules?.[getQuoteConstructionKey(row)] || rules;
    const selected = getStitchingStyle(row);
    const key = getQuoteStitchingKey(row, selected);
    if (key && selected?.config) stitchingConfigs[key] = quote.quoteCalculationSettingsSnapshot?.stitchingConfigs?.[key] || selected.config;
  }
  return { ...normalizeQuoteCalculationSettings(quote), quoteCalculationSettingsSnapshot: { version: 1, fabricRules, stitchingConfigs } };
}

// A physical plan may be recomputed for inspection; it must not reprice a
// frozen legacy row using today's fabric quantities or rates.
export function preserveFrozenConstructionRow(calc, row = {}) {
  const snapshot = calc.calculationSnapshot || {};
  const billable = Number(snapshot.customerBillableFabricMeters ?? snapshot.orderedFabricMeters ?? calc.fabricMeters ?? 0);
  const full = Number(snapshot.fullOrderedFabricMeters ?? snapshot.orderedFabricMeters ?? billable);
  const workshop = Number(snapshot.workshopFabricRequiredMeters ?? snapshot.workshopNewFabricMeters ?? snapshot.newFabricNeededMeters ?? full);
  const lineTotal = Number(snapshot.lineTotal ?? calc.lineTotal ?? calc.rowTotal ?? 0);
  return { ...calc, lining: row.lining || null, liningAmount: Number(snapshot.liningAmount ?? row.liningAmount ?? 0),
    fabricMeters: billable, fabricMetersFull: full, fabricMetersHalf: billable,
    orderedFabricMeters: billable, fullOrderedFabricMeters: full, noReuseOrderedFabricMeters: full,
    customerBillableFabricMeters: billable, workshopNewFabricMeters: workshop, workshopFabricRequiredMeters: workshop,
    newFabricNeededMeters: workshop, cutFabricMeters: Number(snapshot.cutFabricMeters ?? full),
    rowSubtotalBeforeGst: lineTotal, rowTotal: lineTotal, lineTotal, taxableAmount: lineTotal };
}
